// Copyright (c) 2023 MOCA Systems, Inc. All rights reserved.

import { Injectable, inject } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import {
	HttpInterceptor,
	HttpEvent,
	HttpRequest,
	HttpHandler,
	HttpContextToken,
	HttpContext,
	HttpHeaders,
	HttpParams,
} from '@angular/common/http';
import { from, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { LoggerServiceFactory } from '../../common/logging/LoggerServiceFactory';

export enum TouchplanAPIContextAuthentication {
	NoAuthentication = 'NoAuthentication',
	Touchplan = 'Touchplan',
	Custom = 'Custom',
}

export interface ITouchplanAPIContext {
	retryAttempts?: number;
	retryIntervalTime?: number;
	authentication?: TouchplanAPIContextAuthentication;
	customToken?: string;
	responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
	withCredentials?: boolean;
	forceRefresh?: boolean;
}

export interface IUpdateRequestObject<T> {
	headers?: HttpHeaders;
	context?: HttpContext;
	reportProgress?: boolean;
	params?: HttpParams;
	responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
	withCredentials?: boolean;
	body?: T | null;
	method?: string;
	url?: string;
	setHeaders?: {
		[name: string]: string | string[];
	};
	setParams?: {
		[param: string]: string;
	};
}

const TOUCHPLAN_API_CONTEXT_DEFAULT_OPTIONS: ITouchplanAPIContext = {
	authentication: TouchplanAPIContextAuthentication.Touchplan,
	responseType: 'json',
	withCredentials: false,
	retryAttempts: 0,
	retryIntervalTime: 0,
	forceRefresh: false,
};

export const TOUCHPLAN_API_CONTEXT = new HttpContextToken<ITouchplanAPIContext>(
	() => TOUCHPLAN_API_CONTEXT_DEFAULT_OPTIONS
);

/**
 * This class serves as an HttpInterceptor designed to intercept all REST API requests,
 * providing essential functionalities such as authentication handling and request options
 * augmentation.
 */
@Injectable()
export class TouchplanRestInterceptor implements HttpInterceptor {
	private logger = LoggerServiceFactory.create('TouchplanInterceptor');
	private angularFireAuth = inject(AngularFireAuth);

	public intercept(httpRequest: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const [touchplanAPIContext, requestOptions] = this.buildTouchplanRequestConfiguration(
			httpRequest.context.get(TOUCHPLAN_API_CONTEXT)
		);
		return this.addAuthenticationToRequestOptions$(touchplanAPIContext, requestOptions).pipe(
			tap(() => this.logger.log(httpRequest.url)),
			switchMap((requestOptionsWithAuthentication) =>
				next.handle(httpRequest.clone(requestOptionsWithAuthentication))
			)
		);
	}

	private buildTouchplanRequestConfiguration(
		httpRequestContext?: ITouchplanAPIContext
	): [ITouchplanAPIContext, IUpdateRequestObject<unknown>] {
		const touchplanAPIContext: ITouchplanAPIContext = httpRequestContext
			? Object.assign({}, TOUCHPLAN_API_CONTEXT_DEFAULT_OPTIONS, httpRequestContext)
			: TOUCHPLAN_API_CONTEXT_DEFAULT_OPTIONS;
		const requestOptions: IUpdateRequestObject<unknown> = {};

		if (touchplanAPIContext.responseType !== TOUCHPLAN_API_CONTEXT_DEFAULT_OPTIONS.responseType) {
			requestOptions.responseType = touchplanAPIContext.responseType;
		}

		if (touchplanAPIContext.withCredentials !== TOUCHPLAN_API_CONTEXT_DEFAULT_OPTIONS.withCredentials) {
			requestOptions.withCredentials = touchplanAPIContext.withCredentials;
		}

		return [touchplanAPIContext, requestOptions];
	}

	private addAuthenticationToRequestOptions$(
		touchplanAPIContext: ITouchplanAPIContext,
		requestOptions: IUpdateRequestObject<unknown>
	): Observable<IUpdateRequestObject<unknown>> {
		if (
			touchplanAPIContext.authentication === TouchplanAPIContextAuthentication.NoAuthentication ||
			touchplanAPIContext.authentication === TouchplanAPIContextAuthentication.Custom
		) {
			if (touchplanAPIContext.authentication === TouchplanAPIContextAuthentication.Custom) {
				requestOptions.setHeaders = { Authorization: `Bearer ${touchplanAPIContext.customToken}` };
			}
			return of(requestOptions);
		}
		// The Angular Fire Auth compat version is required due to an injection incompatibility issue
		// between the HTTP testing controller and the Angular Fire module version.
		return from(this.angularFireAuth.currentUser).pipe(
			switchMap((user) => {
				if (!user) {
					throw new Error('User not authenticated');
				}
				// Force a refresh of the token to avoid using a token that is about to expire.
				// This minimizes the risk of errors due to expired tokens during long-running requests.
				// This depends on the forceRefresh property in the context.
				return from(user.getIdToken(touchplanAPIContext.forceRefresh));
			}),
			map((authenticationToken) => {
				if (touchplanAPIContext.authentication === TouchplanAPIContextAuthentication.Touchplan) {
					requestOptions.setHeaders = { Authorization: `Bearer ${authenticationToken}` };
				}
				return requestOptions;
			})
		);
	}
}
